<template>
  <section id="home" class="hero">
    <div class="hero-background">
      <!-- 最远的背景层，移动最慢 -->
      <div class="hero-image" :style="{ transform: `translateY(${scrollY * 0.2}px)` }"></div>
      <div class="overlay" :style="{ transform: `translateY(${scrollY * 0.15}px)` }"></div>
      <div class="tech-grid" :style="{ transform: `translateY(${scrollY * 0.25}px)` }"></div>
      <div class="scan-line" :style="{ transform: `translateY(${scrollY * 0.3}px)` }"></div>
      <!-- 中间装饰层，移动速度中等 -->
      <div class="decoration-circle circle-1" :style="{ transform: `translateY(${scrollY * 0.35}px)` }"></div>
      <div class="decoration-circle circle-2" :style="{ transform: `translateY(${scrollY * 0.4}px)` }"></div>
      <div class="decoration-circle circle-3" :style="{ transform: `translateY(${scrollY * 0.38}px)` }"></div>
      <!-- 最近的粒子层，移动最快 -->
      <div class="floating-particles" :style="{ transform: `translateY(${scrollY * 0.45}px)` }">
        <div class="particle particle-1"></div>
        <div class="particle particle-2"></div>
        <div class="particle particle-3"></div>
        <div class="particle particle-4"></div>
        <div class="particle particle-5"></div>
        <div class="particle particle-6"></div>
      </div>
    </div>
    <div class="container">
      <div class="hero-content">
        <div class="hero-card glass-dark">
          <div class="hero-card-inner">
            <p class="hero-subtitle">{{ $t('hero.subtitle') }}</p>
            <h1 class="hero-title">
              {{ $t('hero.title') }}<br>
              <span class="highlight">{{ $t('hero.highlight') }}</span>
            </h1>
            <p class="hero-description">
              {{ $t('hero.description') }}
            </p>
            <div class="hero-actions">
              <button class="btn btn-primary glass-glow" @click="openExpertModal">
                <span class="btn-content">
                  <span class="btn-icon" v-html="starIcon"></span>
                  {{ $t('heroButtons.becomeExpert') }}
                </span>
              </button>
              <button class="btn btn-outline glass-glow" @click="openClientModal">
                <span class="btn-content">
                  <span class="btn-icon" v-html="handshakeIcon"></span>
                  {{ $t('heroButtons.becomeClient') }}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 专家注册弹窗 -->
    <ExpertModal v-if="showExpertModal" @close="closeExpertModal" />

    <!-- 客户注册弹窗 -->
    <ClientModal v-if="showClientModal" @close="closeClientModal" />
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import ExpertModal from './ExpertModal.vue'
import ClientModal from './ClientModal.vue'
import { starIcon, handshakeIcon } from '@/assets/icons.js'

const showExpertModal = ref(false)
const showClientModal = ref(false)
const scrollY = ref(0)
let ticking = false

// 节流更新滚动位置，保证60fps流畅度
const updateScroll = () => {
  if (!ticking) {
    window.requestAnimationFrame(() => {
      // 限制最大移动范围，Hero滚出视口后停止视差，避免性能浪费
      scrollY.value = Math.min(window.scrollY, window.innerHeight)
      ticking = false
    })
    ticking = true
  }
}

const openExpertModal = () => {
  showExpertModal.value = true
}

const closeExpertModal = () => {
  showExpertModal.value = false
}

const openClientModal = () => {
  showClientModal.value = true
}

const closeClientModal = () => {
  showClientModal.value = false
}

onMounted(() => {
  // passive: true 不阻塞页面滚动，提升性能
  window.addEventListener('scroll', updateScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', updateScroll)
})
</script>

<style scoped>
.hero {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;
}

.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -2;
}

/* 视差性能优化：提示浏览器提前进行GPU加速 */
.hero-background > * {
  will-change: transform;
}

/* 无障碍支持：用户开启减少动效时禁用视差效果 */
@media (prefers-reduced-motion: reduce) {
  .hero-background > * {
    transform: none !important;
    will-change: auto;
  }
}

.hero-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('../../.image/hero-bg.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(15, 39, 64, 0.88) 0%, rgba(26, 58, 92, 0.83) 50%, rgba(30, 58, 95, 0.78) 100%);
  z-index: -1;
}

/* Tech grid effect */
.tech-grid {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    linear-gradient(to right, rgba(52, 152, 255, 0.05) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(52, 152, 255, 0.05) 1px, transparent 1px);
  background-size: 80px 80px;
  opacity: 0.3;
  z-index: -1;
  animation: gridDrift 20s linear infinite;
}

@keyframes gridDrift {
  0% { transform: translate(0, 0); }
  100% { transform: translate(40px, 40px); }
}

/* Scan line effect */
.scan-line {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg,
    transparent 0%,
    rgba(52, 152, 255, 0.2) 50%,
    transparent 100%);
  z-index: -1;
  animation: scanMove 8s ease-in-out infinite;
  filter: blur(1px);
}

@keyframes scanMove {
  0% { top: 0; opacity: 0; }
  10% { opacity: 0.5; }
  90% { opacity: 0.5; }
  100% { top: 100%; opacity: 0; }
}

/* Floating particles */
.floating-particles {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -1;
  pointer-events: none;
}

.particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background: rgba(52, 152, 255, 0.6);
  border-radius: 50%;
  filter: blur(1px);
  animation: float 12s ease-in-out infinite;
}

.particle::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 12px;
  height: 12px;
  background: rgba(52, 152, 255, 0.1);
  border-radius: 50%;
}

.particle-1 {
  top: 20%;
  left: 15%;
  animation-delay: 0s;
}

.particle-2 {
  top: 60%;
  right: 20%;
  animation-delay: 2s;
  width: 6px;
  height: 6px;
}

.particle-3 {
  top: 40%;
  left: 70%;
  animation-delay: 4s;
  width: 3px;
  height: 3px;
}

.particle-4 {
  top: 70%;
  left: 30%;
  animation-delay: 6s;
}

.particle-5 {
  top: 30%;
  right: 40%;
  animation-delay: 8s;
  width: 5px;
  height: 5px;
}

.particle-6 {
  top: 80%;
  right: 10%;
  animation-delay: 10s;
}

@keyframes float {
  0%, 100% {
    transform: translate(0, 0) scale(1);
    opacity: 0.4;
  }
  25% {
    transform: translate(30px, -30px) scale(1.2);
    opacity: 0.7;
  }
  50% {
    transform: translate(60px, 20px) scale(0.8);
    opacity: 0.3;
  }
  75% {
    transform: translate(20px, 40px) scale(1.1);
    opacity: 0.6;
  }
}

/* Decorative circles */
.decoration-circle {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.3;
  pointer-events: none;
  animation: pulse 12s ease-in-out infinite;
}

.circle-1 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, color-mix(in srgb, var(--secondary-color) 40%, transparent) 0%, transparent 70%);
  top: -10%;
  right: 0%;
  animation-delay: 0s;
}

.circle-2 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, color-mix(in srgb, var(--primary-light) 30%, transparent) 0%, transparent 70%);
  bottom: -10%;
  left: 10%;
  animation-delay: 4s;
}

.circle-3 {
  width: 350px;
  height: 350px;
  background: radial-gradient(circle, color-mix(in srgb, var(--white) 12%, transparent) 0%, transparent 70%);
  top: 30%;
  right: 30%;
  animation-delay: 8s;
}

@keyframes pulse {
  0%, 100% { transform: scale(1) translate(0, 0); opacity: 0.3; }
  33% { transform: scale(1.1) translate(30px, -20px); opacity: 0.45; }
  66% { transform: scale(0.95) translate(-20px, 30px); opacity: 0.35; }
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
  width: 100%;
}

@media (max-width: 1366px) {
  .container {
    padding: 0 32px;
  }
}

@media (max-width: 1024px) {
  .container {
    padding: 0 24px;
  }
}

@media (max-width: 768px) {
  .container {
    padding: 0 16px;
  }
}

.hero-content {
  width: 100%;
  color: white;
  text-align: left;
  padding-top: 100px;
}

.hero-card {
  display: inline-block;
  border-radius: var(--rounded-lg); /* 设计系统大圆角 */
  padding: var(--spacing-lg) var(--spacing-xl); /* 设计系统标准内边距 */
  position: relative;
  overflow: hidden;
}

.hero-card::before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(135deg,
    rgba(52, 152, 255, 0.5) 0%,
    rgba(52, 152, 255, 0.2) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(52, 152, 255, 0.2) 75%,
    rgba(52, 152, 255, 0.5) 100%);
  border-radius: 32px;
  z-index: -1;
  opacity: 0.7;
  animation: borderGlow 6s ease-in-out infinite;
}

.hero-card::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.4) 50%,
    transparent 100%);
}

@keyframes borderGlow {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 0.8; }
}

.hero-card-inner {
  position: relative;
  z-index: 1;
}

.hero-subtitle {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--primary-blue); /* 设计系统标准命名 */
  margin-bottom: var(--spacing-sm);
  letter-spacing: 3px;
  text-transform: uppercase; /* 符合设计系统label规范 */
  opacity: 0.95;
  line-height: 1.4;
}

.hero-title {
  font-size: clamp(2.5rem, 5vw, 3.8rem); /* 设计系统display级字体 */
  font-weight: 700;
  line-height: 1.15;
  margin-bottom: 24px;
  text-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}

.hero-title .highlight {
  color: var(--secondary-color);
  font-weight: 700;
}


.hero-description {
  font-size: clamp(1rem, 1.5vw, 1.1rem); /* 设计系统body级字体 */
  line-height: 1.8;
  opacity: 0.9;
  margin-bottom: var(--spacing-md);
  max-width: 65ch; /* 符合设计系统行宽规则 */
  color: rgba(255, 255, 255, 0.92);
}

.hero-actions {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.btn {
  padding: 0;
  border: none;
  border-radius: var(--rounded-md); /* 设计系统中等圆角 */
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 3px;
  text-transform: uppercase;
  cursor: pointer;
  overflow: hidden;
  position: relative;
}

.btn-content {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: var(--spacing-xs) 36px; /* 设计系统标准按钮内边距 */
  position: relative;
  z-index: 1;
}

.btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
}

.btn-icon svg {
  width: 20px;
  height: 20px;
}

.btn-primary {
  background: linear-gradient(135deg, var(--secondary-color) 0%, var(--primary-light) 100%);
  color: var(--white);
  box-shadow:
    0 4px 20px rgba(52, 152, 255, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.1) inset;
}

.btn-primary .btn-content {
  transition: all 0.3s ease;
}

.btn-primary:hover .btn-content {
  transform: translateY(-2px);
}

.btn-primary:hover {
  box-shadow:
    0 8px 30px rgba(52, 152, 255, 0.45),
    0 0 0 1px rgba(255, 255, 255, 0.15) inset;
}

.btn-primary:active .btn-content {
  transform: translateY(0);
}

.btn-outline {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.25);
  box-shadow:
    0 4px 20px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);
}

.btn-outline .btn-content {
  transition: all 0.3s ease;
}

.btn-outline:hover {
  background: rgba(255, 255, 255, 0.18);
  border-color: rgba(52, 152, 255, 0.5);
  box-shadow:
    0 8px 30px rgba(0, 0, 0, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.2),
    0 0 20px rgba(52, 152, 255, 0.15);
}

.btn-outline:hover .btn-content {
  transform: translateY(-2px);
}

.btn-outline:active .btn-content {
  transform: translateY(0);
}

@media (max-width: 1366px) {
  .hero-title {
    font-size: 3.2rem;
  }

  .hero-description {
    font-size: 1.15rem;
  }

  .hero-card {
    padding: 50px 60px;
  }
}

@media (max-width: 1024px) {
  .hero-title {
    font-size: 2.8rem;
  }

  .hero-description {
    font-size: 1.1rem;
  }

  .hero-card {
    padding: 45px 50px;
  }

  .btn-content {
    padding: 14px 30px;
  }
}

@media (max-width: 768px) {
  .hero-content {
    padding-top: 80px;
  }

  .hero-card {
    padding: 35px 28px;
    border-radius: 24px;
  }

  .hero-title {
    font-size: 2.2rem;
  }

  .hero-description {
    font-size: 1rem;
  }

  .hero-actions {
    flex-direction: column;
    gap: 14px;
  }

  .btn {
    width: 100%;
  }

  .btn-content {
    padding: 14px 28px;
    justify-content: center;
  }

  .hero-subtitle {
    font-size: 0.95rem;
    letter-spacing: 2px;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 1.9rem;
  }

  .hero-card {
    padding: 30px 24px;
  }

  .hero-subtitle {
    font-size: 0.9rem;
  }

  .hero-description {
    font-size: 0.95rem;
  }

  .tech-grid {
    background-size: 60px 60px;
  }

  .particle {
    display: none;
  }
}

/* Reduce motion support */
@media (prefers-reduced-motion: reduce) {
  .tech-grid,
  .scan-line,
  .particle,
  .decoration-circle {
    animation: none !important;
  }

  .scan-line {
    display: none;
  }
}
</style>
